import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Server } from 'lucide-react';
import { fetchIntegrationAgents, type IntegrationAgent } from '../../api/dataApi';
import { ScreenHeader } from '../../components/ui/ScreenHeader';
import { AppCard } from '../../components/ui/AppCard';
import { EmptyState } from '../../components/ui/EmptyState';
import { HealthPill } from '../../components/integrations/HealthPill';
import { relativeAgo } from '../../lib/integrationHealth';
import { AgentSetupHelp } from '../../components/integrations/AgentSetupHelp';

// Agents (roadmap #9 Phase 5): the cue-cli instances running this account's
// agent-side integrations (Prometheus scrape, JSON, CSV, local files). An
// agent is known only by what it reports with each run - a name, a version and
// when it last ran - because the cloud never reaches into a customer's
// network to check. Since agent 0.5.0 it also reports its retry-spool backlog
// and which Mapping version each integration last ran, so the page can show a
// buffering agent and configuration drift; an older agent omits both and is
// shown as such (unknown), never as healthy-by-default.
const formatBytes = (n: number): string => (n < 1024 ? `${n} B` : n < 1024 * 1024 ? `${(n / 1024).toFixed(1)} KB` : `${(n / 1024 / 1024).toFixed(1)} MB`);

export function AgentsScreen() {
  const [agents, setAgents] = useState<IntegrationAgent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    fetchIntegrationAgents()
      .then((a) => alive && setAgents(a))
      .catch((e) => alive && setError(e instanceof Error ? e.message : 'Unable to load agents.'));
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="flex flex-col gap-4">
      <ScreenHeader title="Agents" backTo="/integrations" />
      <AgentSetupHelp />
      {error ? (
        <p className="text-sm text-red-500">{error}</p>
      ) : agents === null ? (
        <div className="py-10 text-center text-sm text-black/40 dark:text-white/40">
          <Loader2 size={14} className="inline animate-spin" /> Loading…
        </div>
      ) : agents.length === 0 ? (
        <EmptyState
          icon={Server}
          title="No agents yet"
          subtitle="Run cue-cli import or cue-cli daemon --import on a host that can reach your sources. It appears here after its first run."
        />
      ) : (
        agents.map((a) => (
          <AppCard key={a.name} className="flex flex-col gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold">{a.name}</span>
              <span className="rounded-md bg-black/[0.06] dark:bg-white/10 text-black/60 dark:text-white/60 text-[11px] font-mono px-1.5 py-0.5">
                v{a.version}
              </span>
              <span
                className={`inline-flex items-center gap-1 text-[11px] font-medium ${a.online ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${a.online ? 'bg-emerald-500' : 'bg-amber-500'}`} aria-hidden />
                {a.online ? 'Online' : 'Offline'}
              </span>
              <div className="flex-1" />
              <span className="text-[11px] text-black/40 dark:text-white/40">last heard {relativeAgo(a.lastSeenAt)}</span>
            </div>
            {a.outdated && (
              <span role="status" className="text-xs text-amber-600 dark:text-amber-400">
                Update available: this agent is v{a.version}, latest is v{a.latestVersion}.
              </span>
            )}
            {a.spoolReported && (a.spoolDepth ?? 0) > 0 && (
              <span role="status" className="text-xs text-amber-600 dark:text-amber-400">
                {a.spoolDepth} Signal{a.spoolDepth === 1 ? '' : 's'} queued on this agent ({formatBytes(a.spoolBytes ?? 0)}) - the backend
                could not take them yet. They are delivered automatically once it can.
              </span>
            )}
            {!a.online && (
              <span className="text-xs text-black/50 dark:text-white/50">
                It has stopped reporting. Check that the agent is still running on its host (see the status commands above).
              </span>
            )}
            {a.mappings?.length === 0 ? (
              <span className="text-xs text-black/40 dark:text-white/40">No integrations reported yet.</span>
            ) : (
              <div className="flex flex-col divide-y divide-black/5 dark:divide-white/5">
                {a.mappings?.map((m) => (
                  <Link key={m.nickname} to={`/integrations/${encodeURIComponent(m.nickname)}`} className="flex items-center gap-2 py-2 text-sm">
                    <span className="truncate flex-1">{m.title}</span>
                    {m.drift && (
                      <span
                        className="text-[11px] font-medium text-amber-600 dark:text-amber-400"
                        title="This agent last ran an older version than the one published. It picks up the new version on its next run."
                      >
                        running v{m.runningVersion}, published v{m.activeVersion}
                      </span>
                    )}
                    <span className="text-[11px] text-black/40 dark:text-white/40">{relativeAgo(m.lastRunAt)}</span>
                    <HealthPill state={m.health.state} title={m.health.reason} />
                  </Link>
                ))}
              </div>
            )}
          </AppCard>
        ))
      )}
    </div>
  );
}
